import styled from '@emotion/styled';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Message } from './Message';

import type { SnackbarStackProps, SnackbarStackRef, SnackbarAlert } from './SnackbarStack.types';

/**
 * Container for stacking snackbars
 * Uses pointer-events: none to allow clicks to pass through to elements beneath
 */
const Container = styled.div<{
  $position: SnackbarStackProps['position'];
}>`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1400;
  pointer-events: none;

  /* Position-based styles */
  ${({ $position }) => {
    switch ($position) {
      case 'top-left':
        return `
          top: 16px;
          left: 16px;
          align-items: flex-start;
        `;
      case 'top-center':
        return `
          top: 16px;
          left: 50%;
          transform: translateX(-50%);
          align-items: center;
        `;
      case 'top-right':
        return `
          top: 16px;
          right: 16px;
          align-items: flex-end;
        `;
      case 'bottom-left':
        return `
          bottom: 16px;
          left: 16px;
          align-items: flex-start;
        `;
      case 'bottom-right':
        return `
          bottom: 16px;
          right: 16px;
          align-items: flex-end;
        `;
      case 'bottom-center':
      default:
        return `
          bottom: 16px;
          left: 50%;
          transform: translateX(-50%);
          align-items: center;
        `;
    }
  }}
`;

/**
 * Wrapper for individual snackbar to enable pointer events
 */
const SnackbarWrapper = styled.div`
  pointer-events: auto;
`;

/**
 * SnackbarStack - A component that manages and displays multiple stacking snackbars
 *
 * This component renders snackbars in a fixed position on the screen and handles
 * their lifecycle including auto-dismiss timers and animations.
 *
 * @example
 * ```tsx
 * const [alerts, setAlerts] = useState<Map<string, SnackbarAlert>>(new Map());
 *
 * const addAlert = (alert: SnackbarAlert) => {
 *   setAlerts(prev => new Map(prev).set(alert.id, alert));
 * };
 *
 * const removeAlert = (id: string) => {
 *   setAlerts(prev => {
 *     const next = new Map(prev);
 *     next.delete(id);
 *     return next;
 *   });
 * };
 *
 * <SnackbarStack
 *   alerts={alerts}
 *   onClose={removeAlert}
 *   position="bottom-center"
 * />
 * ```
 */
export const SnackbarStack = forwardRef<SnackbarStackRef, SnackbarStackProps>(
  (
    {
      alerts,
      onAction,
      onClose,
      className,
      position = 'bottom-center',
      maxWidth,
      dataTestId,
    },
    ref
  ) => {
    // Track alerts that are being rendered (includes those being removed)
    const [renderedAlerts, setRenderedAlerts] = useState<Map<string, SnackbarAlert>>(new Map());
    // Track alerts that are in the process of being removed (for exit animation)
    const [exitingAlerts, setExitingAlerts] = useState<Set<string>>(new Set());
    // Track timers for auto-dismiss
    const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    // Clear a specific timer
    const clearTimer = useCallback((id: string) => {
      if (timersRef.current[id]) {
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
      }
    }, []);

    // Handle closing an alert
    const handleClose = useCallback(
      (id: string) => {
        clearTimer(id);

        // Start exit animation
        setExitingAlerts((prev) => new Set(prev).add(id));

        // Remove after animation completes
        setTimeout(() => {
          setRenderedAlerts((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
          });
          setExitingAlerts((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, 300); // Match animation duration

        if (onClose) {
          onClose(id);
        }
      },
      [clearTimer, onClose]
    );

    // Expose closeAlert method via ref
    useImperativeHandle(
      ref,
      () => ({
        closeAlert: handleClose,
      }),
      [handleClose]
    );

    // Handle new alerts and set up timers
    useEffect(() => {
      // Find new alerts that aren't already rendered
      alerts.forEach((alert, id) => {
        if (!renderedAlerts.has(id)) {
          // Add to rendered alerts
          setRenderedAlerts((prev) => new Map(prev).set(id, alert));

          // Set up auto-dismiss timer if duration is not explicitly disabled
          // autoHideDuration of null or 0 disables auto-dismiss
          if (alert.autoHideDuration !== null && alert.autoHideDuration !== 0) {
            const duration = alert.autoHideDuration ?? 5000;
            clearTimer(id);
            timersRef.current[id] = setTimeout(() => {
              handleClose(id);
            }, duration);
          }
        }
      });

      // Find alerts that were removed from props but are still rendered
      renderedAlerts.forEach((_, id) => {
        if (!alerts.has(id) && !exitingAlerts.has(id)) {
          // Start exit animation for removed alerts
          clearTimer(id);
          setExitingAlerts((prev) => new Set(prev).add(id));
          
          // Remove after animation completes
          setTimeout(() => {
            setRenderedAlerts((prev) => {
              const next = new Map(prev);
              next.delete(id);
              return next;
            });
            setExitingAlerts((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }, 300);
        }
      });
    }, [alerts, renderedAlerts, exitingAlerts, clearTimer, handleClose]);

    // Clean up all timers on unmount
    useEffect(() => {
      return () => {
        Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
        timersRef.current = {};
      };
    }, []);

    // Handle action button click
    const handleAction = useCallback(
      (id: string, alertAction?: SnackbarAlert['action']) => {
        if (alertAction?.onClick) {
          alertAction.onClick();
        }
        if (onAction) {
          onAction(id);
        }
      },
      [onAction]
    );

    // Handle mouse enter - pause timer
    const handleMouseEnter = useCallback(
      (id: string) => {
        clearTimer(id);
      },
      [clearTimer]
    );

    // Handle mouse leave - restart timer
    const handleMouseLeave = useCallback(
      (id: string, alert: SnackbarAlert) => {
        // Don't restart timer if auto-dismiss is disabled
        if (alert.autoHideDuration === null || alert.autoHideDuration === 0) {
          return;
        }
        const duration = alert.autoHideDuration ?? 5000;
        if (!exitingAlerts.has(id)) {
          timersRef.current[id] = setTimeout(() => {
            handleClose(id);
          }, duration);
        }
      },
      [exitingAlerts, handleClose]
    );

    // Don't render if no alerts
    if (renderedAlerts.size === 0) {
      return null;
    }

    const content = (
      <Container $position={position} className={className} data-testid={dataTestId}>
        {Array.from(renderedAlerts.entries()).map(([id, alert]) => {
          const isExiting = exitingAlerts.has(id);

          return (
            <SnackbarWrapper
              key={id}
              onMouseEnter={() => handleMouseEnter(id)}
              onMouseLeave={() => handleMouseLeave(id, alert)}
            >
              <Message
                variant={alert.variant}
                mode="snackbar"
                open={!isExiting}
                isExiting={isExiting}
                showCloseButton={alert.showCloseButton ?? true}
                onClose={() => handleClose(id)}
                action={
                  alert.action
                    ? {
                        label: alert.action.label,
                        onClick: () => handleAction(id, alert.action),
                      }
                    : undefined
                }
                maxWidth={maxWidth}
                autoHideDuration={null} // We handle timers in SnackbarStack
                dataTestId={`snackbar-${id}`}
              >
                {alert.message}
              </Message>
            </SnackbarWrapper>
          );
        })}
      </Container>
    );

    // Use portal to render at document body level
    if (typeof document !== 'undefined') {
      return createPortal(content, document.body);
    }

    return content;
  }
);

SnackbarStack.displayName = 'SnackbarStack';

export default SnackbarStack;
